import { useTheme } from "@material-ui/core";
import { transparentize } from "polished";
import { GroupBase, StylesConfig, ThemeConfig } from "react-select5";
import { Color } from "../../../../theme/color";

type Props = {
  menu?: {
    showBorder?: boolean;
  } & React.CSSProperties;
  control?: {
    showBorder?: boolean;
  } & React.CSSProperties;
  menuPortal?: React.CSSProperties;
  menuList?: React.CSSProperties;
  placeholder?: React.CSSProperties;
  container?: React.CSSProperties;
  option?: React.CSSProperties;
  multiValue?: React.CSSProperties;
  multiValueLabel?: React.CSSProperties;
  groupHeading?: React.CSSProperties;
  indicatorsContainer?: React.CSSProperties;
  valueContainer?: React.CSSProperties;
};

export const useSelectStyle = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props?: Props
) => {
  const theme = useTheme();

  const selectStyle: StylesConfig<Option, IsMulti, Group> = {
    container: (base) => ({
      ...base,
      fontFamily: theme.typography.fontFamily,
      ...(props?.container || {})
    }),
    menuPortal: (base) => ({
      ...base,
      fontFamily: theme.typography.fontFamily,
      ...(props?.menuPortal || {})
    }),
    menuList: (base) => ({
      ...base,
      ...(props?.menuList || {}),
      contentVisibility: "auto",
      containIntrinsicSize: "auto 550px"
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      backgroundColor: "transparent",
      color: "inherit",
      opacity: state.isDisabled ? "50%" : undefined,
      fontSize: theme.typography.body2.fontSize,
      ":active": {
        backgroundColor: `${transparentize(0.85, theme.palette.primary.main)}`
      },
      ":hover": {
        backgroundColor: `${transparentize(0.85, theme.palette.primary.main)}`
      },
      ...(props?.option || {})
    }),
    multiValue: (baseStyles) => ({
      ...baseStyles,
      backgroundColor: transparentize(0.75, Color.DoveBlue),
      border: `0.5px solid ${Color.DoveBlue}`,
      padding: "0 0.25rem",
      fontSize: theme.typography.subtitle1.fontSize,
      fontStyle: theme.typography.subtitle1.fontStyle,
      fontWeight: theme.typography.subtitle1.fontWeight,
      lineHeight: theme.typography.subtitle1.lineHeight,
      ...(props?.multiValue || {})
    }),
    multiValueLabel: (baseStyles) => ({
      ...baseStyles,
      fontSize: theme.typography.subtitle1.fontSize,
      fontStyle: theme.typography.subtitle1.fontStyle,
      fontWeight: theme.typography.subtitle1.fontWeight,
      lineHeight: theme.typography.subtitle1.lineHeight,
      ...(props?.multiValueLabel || {})
    }),
    groupHeading: (baseStyles) => ({
      ...baseStyles,
      color: Color.Headings,
      textTransform: "none",
      ...(props?.groupHeading || {})
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      backgroundColor: theme.palette.background.default,
      border: props?.menu?.showBorder ? baseStyles.border : "none",
      boxShadow: props?.menu?.showBorder ? baseStyles.boxShadow : "none",
      zIndex: props?.menu?.zIndex !== undefined ? props?.menu?.zIndex : 999,
      position: props?.menu?.position ? props?.menu?.position : "absolute",
      height: props?.menu?.height ? props?.menu?.height : undefined,
      width: "auto",
      minWidth: "100%",
      ...(props?.menu || {})
    }),
    control: (baseStyles) => ({
      ...baseStyles,
      minHeight: "2.5rem",
      border: props?.control?.showBorder ? baseStyles.border : "none",
      borderRadius: "8px",
      boxShadow: props?.control?.showBorder ? baseStyles.boxShadow : "none",
      "&:hover": {
        ...baseStyles[":hover"],
        cursor: "pointer"
      },
      ...(props?.control || {})
    }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      ...(theme.typography.body1 as any),
      color: Color.DetailsInput,
      ...(props?.placeholder || {})
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: "0.5rem",
      ...(props?.indicatorsContainer || {})
    }),
    valueContainer: (base) => ({
      ...base,
      gridGap: "0.25rem",
      display: "flex",
      fontSize: theme.typography.body1.fontSize,
      ...(props?.valueContainer || {})
    })
  };

  const selectTheme: Partial<ThemeConfig> = {
    colors: {
      primary: theme.palette.primary.main,
      neutral20: theme.palette.info.light,
      neutral10: transparentize(0.5, theme.palette.info.light)
    }
  };
  return { selectStyle, selectTheme };
};
